import Container from '@components/ui/Container/Container'

import { isMobile, isTablet } from '@utils/helpers/DeviceUtils'
import cn, { clsx } from 'clsx'
import { CSSProperties, FC, useEffect, useState } from 'react'
import { ModuleProps } from '..'

import { BlogPostFragment } from '@lib/umbraco/generated/types.generated'
import { useQueryState } from 'next-usequerystate'
import { useRouter } from 'next/router'
import BlogPost, { BlogPostProps } from '../BlogPost/BlogPost'
import BlogPostSlider from '../BlogPostSlider/BlogPostSlider'
import styles from './BlogPostGrid.module.scss'

type BlogPostGridProps = {
  blogPosts?: BlogPostProps[]
  columnCount?: number
  maxWidth?: number
  enableDesktopSlider?: boolean
  enableMobileSlider?: boolean
  disableBodyText?: boolean
  backgroundColorPicker?: string
  controls?: boolean
  categories?: string[]
} & ModuleProps

const BlogPostGrid: FC<BlogPostGridProps> = ({
  blogPosts,
  maxWidth,
  columnCount: columns = 4,
  enableDesktopSlider,
  enableMobileSlider,
  disableBodyText,
  controls,
  backgroundColorPicker,
  lcp,
}) => {
  const router = useRouter()
  const [categoryFilter, _] = useQueryState('category')
  const [slider, setSlider] = useState(false)

  // useEffect(() => {}, [])
  useEffect(() => {
    //resize observer to check if the slider should be enabled
    const resizeObserver = new ResizeObserver(() => {
      setSlider(
        (enableMobileSlider && isMobile() && !isTablet()) ||
          (enableDesktopSlider && !isMobile()),
      )
    })
    resizeObserver.observe(document.body)
    return () => {
      resizeObserver.disconnect()
    }
  }, [])
  const categories = [
    ...new Set(
      blogPosts
        ?.map((post) => post.categories)
        .flat()
        .map((x) => x.toLowerCase()),
    ),
  ]
  const setSelectedCategory = (category: string) => {
    // if category is already selected, remove filter
    if (categoryFilter === category) {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            category: null,
          },
        },
        undefined,
        { scroll: false },
      )
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            category,
          },
        },
        undefined,
        { scroll: false },
      )
    }
  }

  useEffect(() => {
    const active = document.querySelector(`.${styles.active}`)
    if (active) active.classList.remove(styles.active)
    const newActive = document.querySelector(
      `[data-category="${categoryFilter?.length > 0 ? categoryFilter : 'all'}"]`,
    )
    if (newActive) newActive.classList.add(styles.active)
  }, [categoryFilter])

  return (
    <div
      className={styles.root}
      data-module="blog-post-grid"
      style={{ '--bg-color': backgroundColorPicker } as CSSProperties}
    >
      {blogPosts && slider ? (
        <BlogPostSlider
          blogPosts={blogPosts as BlogPostFragment[]}
          controls={controls}
          hideDescriptions={disableBodyText}
        />
      ) : (
        <Container className={styles.maxContainer}>
          {categories && categories.length > 0 && (
            <div className={styles.categoryContainer}>
              <ul className={styles.categoryWrapper}>
                <li
                  className={clsx({
                    [styles.active]: router.query.category?.length === 0,
                  })}
                  data-category="all"
                  onClick={() => setSelectedCategory(null)}
                >
                  <span className={styles.underlineAnimation}>All</span>
                </li>
                {categories
                  .sort((a, b) => a.localeCompare(b))
                  .map((category) => (
                    <li
                      className={clsx({
                        [styles.active]: router.query.category === category,
                      })}
                      key={category}
                      data-category={category}
                      onClick={() => setSelectedCategory(category)}
                    >
                      <span className={styles.underlineAnimation}>
                        {category}
                      </span>
                    </li>
                  ))}
              </ul>
              <div className={styles.scrollMore} />
            </div>
          )}

          <div
            style={{
              maxWidth: maxWidth ? `${maxWidth}vw` : 'unset',
            }}
            className={cn(styles.container)}
          >
            {blogPosts &&
              blogPosts
                .filter((post) =>
                  categoryFilter || router.query.category
                    ? post.categories
                        .map((x) => x.toLowerCase())
                        .includes(
                          categoryFilter || (router.query.category as string),
                        )
                    : true,
                )
                .map((blogPost, index) => (
                  <BlogPost
                    itemWidth={columns ? 1 / columns : 1 / 3}
                    disableBodyText={disableBodyText}
                    sliderItem={false}
                    key={blogPost.title}
                    priority={lcp && index < 3 ? true : false}
                    {...blogPost}
                  />
                ))}
          </div>
        </Container>
      )}
    </div>
  )
}

export default BlogPostGrid

export const generateSlides = (
  size: number,
  slides: HTMLElement[],
  perView: number,
  spacing: number,
) => {
  spacing = spacing / size || 0
  const spacingPortion = spacing / perView

  let checkedLength = 0
  let length =
    (1 / perView - spacing + spacingPortion) * slides.length +
    spacing * (slides.length - 1)

  return slides.map((_, index) => {
    const slideSize = 1 / perView - spacing + spacingPortion
    const space = length - checkedLength
    checkedLength += slideSize + spacing
    if (index === 0)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 0,
      }

    if (space >= 1)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 0,
      }
    if (index === slides.length - 1)
      return {
        size: slideSize,
        spacing: spacing,
        origin: 1 - space - spacing - 0.06,
      }
    return {
      size: slideSize,
      spacing: spacing,
      origin: 1 - space - (length > 1 ? 0 : 1 - length),
    }
  })
}
